/**
 * UnterschriftenStep — Kiosk-Wizard Schritt 13
 * Multi-Dokument-Unterschriften mit Progress-Bar (0/N → N/N)
 *
 * Flow pro Dokument: Dokument-Liste → Lesen (Scroll-Gate) → Signatur-Canvas → Weiter
 * Refs: #629
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import { CheckCircle2, FileText, Pen, Trash2, ChevronRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

// ── Types ────────────────────────────────────────────────────────────────────

type DokumentId = 'gutachten' | 'demontage' | 'mietwagen' | 'anwalt';
type Phase = 'liste' | 'lesen' | 'unterschreiben';

interface DokumentDef {
  id: DokumentId;
  titel: string;
  untertitel: string;
  inhalt: React.ReactNode;
}

interface Props {
  sessionId: string;
  kundenName: string;
  kennzeichen: string;
  demontageNoetig: boolean;
  ersatzfahrzeugGewaehlt: boolean;
  anwaltName?: string;
  onComplete: (unterschriften: Record<string, string>) => void;
  onBack: () => void;
}

// ── Dokument-Inhalte ─────────────────────────────────────────────────────────

function InhaltGutachten({ kundenName, kennzeichen }: { kundenName: string; kennzeichen: string }) {
  return (
    <div className="space-y-4 text-[12px] text-gray-700 leading-relaxed">
      <div className="border-b pb-3" style={{ borderColor: '#C41E3A' }}>
        <p className="font-bold text-[14px] text-gray-900">Kfz Sachverständigenbüro Koop</p>
        <p className="text-gray-500 text-[11px]">Rahmedestraße 65 · 58507 Lüdenscheid · Tel: 02351 / 87 69 860</p>
      </div>
      <p className="font-bold text-[13px] text-center text-gray-900">
        Auftrag zur Gutachtenerstellung und Honorarvereinbarung
      </p>
      <div className="bg-gray-50 rounded-lg p-3">
        <p><span className="font-semibold">Auftraggeber:</span> {kundenName || '—'}</p>
        <p><span className="font-semibold">Kennzeichen:</span> {kennzeichen || '—'}</p>
        <p><span className="font-semibold">Datum:</span> {new Date().toLocaleDateString('de-DE')}</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Auftrag</p>
        <p>Ich beauftrage das Kfz-Sachverständigenbüro Koop mit der Erstellung eines Gutachtens zur Schadenshöhe. Das Honorar richtet sich nach der BVSK-Honorartabelle zuzüglich Nebenkosten und gesetzlicher MwSt.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Zahlungsanweisung</p>
        <p>Ich weise an, dass die Erstattung des Gutachterhonorars aus den Schadenersatzansprüchen direkt an das Sachverständigenbüro Koop erfolgt. Die Forderung wird nicht abgetreten.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Datenschutz</p>
        <p>Personenbezogene Daten können im Rahmen des Auftrags an Dritte weitergeleitet werden. Ich kann meine Einwilligung jederzeit widerrufen.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Widerrufsrecht</p>
        <p>Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen. Ich erkläre ausdrücklich mein Einverständnis, dass vor Ablauf der Widerrufsfrist mit der Ausführung der Dienstleistung begonnen wird.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Honorarvereinbarung</p>
        <p>Die Abrechnung erfolgt in Anlehnung an die BVSK-Honorarbefragung. Nebenkosten werden nach JVEG berechnet.</p>
      </div>
      <p className="text-[11px] text-gray-500 italic">Mit meiner Unterschrift bestätige ich die Kenntnisnahme und Zustimmung zu den oben genannten Bedingungen.</p>
    </div>
  );
}

function InhaltDemontage({ kundenName, kennzeichen }: { kundenName: string; kennzeichen: string }) {
  return (
    <div className="space-y-4 text-[12px] text-gray-700 leading-relaxed">
      <div className="border-b pb-3" style={{ borderColor: '#C41E3A' }}>
        <p className="font-bold text-[14px] text-gray-900">Demontage- und Vermessungsauftrag</p>
        <p className="text-gray-500 text-[11px]">LCS Werkstatt · Im Auftrag von Gutachter Koop</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3">
        <p><span className="font-semibold">Auftraggeber:</span> {kundenName || '—'}</p>
        <p><span className="font-semibold">Fahrzeug:</span> {kennzeichen || '—'}</p>
        <p><span className="font-semibold">Datum:</span> {new Date().toLocaleDateString('de-DE')}</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Auftrag</p>
        <p>Ich beauftrage die teilweise Demontage meines Fahrzeugs zum Zweck der genauen Schadenbegutachtung. Nach Abschluss der Begutachtung wird das Fahrzeug fachgerecht wieder montiert und die Verkehrssicherheit hergestellt.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Ersatzfahrzeug</p>
        <p>Während der Demontage- und Vermessungsphase wird mir ein kostenloses Ersatzfahrzeug von MietGo zur Verfügung gestellt.</p>
      </div>
      <p className="text-[11px] text-gray-500 italic">Mit meiner Unterschrift erteile ich die Genehmigung zur Demontage meines Fahrzeugs für Begutachtungszwecke.</p>
    </div>
  );
}

function InhaltMietwagen({ kundenName, kennzeichen }: { kundenName: string; kennzeichen: string }) {
  return (
    <div className="space-y-4 text-[12px] text-gray-700 leading-relaxed">
      <div className="border-b pb-3" style={{ borderColor: '#C41E3A' }}>
        <p className="font-bold text-[14px] text-gray-900">Mietwagenvertrag MietGo + Abtretungserklärung</p>
        <p className="text-gray-500 text-[11px]">MietGo GmbH · Im Rahmen des Unfallersatzes</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3">
        <p><span className="font-semibold">Mieter:</span> {kundenName || '—'}</p>
        <p><span className="font-semibold">Fahrzeug:</span> {kennzeichen || '—'}</p>
        <p><span className="font-semibold">Datum:</span> {new Date().toLocaleDateString('de-DE')}</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Mietvertrag</p>
        <p>Ich erhalte ein Ersatzfahrzeug der Mietwagenklasse meines beschädigten Fahrzeugs. Die Kosten werden direkt mit der gegnerischen Haftpflichtversicherung abgerechnet. Voraussetzung ist das Vorliegen eines Haftpflichtschadens.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Abtretungserklärung</p>
        <p>Ich trete hiermit meine Schadensersatzforderung für Mietwagenkosten gegen den Schädiger und dessen Versicherung in Höhe der tatsächlich angefallenen und erstattungsfähigen Mietwagenkosten an MietGo GmbH ab.</p>
      </div>
      <p className="text-[11px] text-gray-500 italic">Mit meiner Unterschrift bestätige ich die Kenntnisnahme des Mietvertrags und der Abtretungserklärung.</p>
    </div>
  );
}

function InhaltAnwalt({ kundenName, anwaltName }: { kundenName: string; anwaltName?: string }) {
  return (
    <div className="space-y-4 text-[12px] text-gray-700 leading-relaxed">
      <div className="border-b pb-3" style={{ borderColor: '#C41E3A' }}>
        <p className="font-bold text-[14px] text-gray-900">Anwaltsvollmacht</p>
        <p className="text-gray-500 text-[11px]">Vollmacht für den Korrespondenzanwalt meiner Wahl</p>
      </div>
      <div className="bg-gray-50 rounded-lg p-3">
        <p><span className="font-semibold">Vollmachtgeber:</span> {kundenName || '—'}</p>
        {anwaltName && <p><span className="font-semibold">Bevollmächtigter:</span> {anwaltName}</p>}
        <p><span className="font-semibold">Datum:</span> {new Date().toLocaleDateString('de-DE')}</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Vollmacht</p>
        <p>Ich erteile {anwaltName ? <><strong>{anwaltName}</strong>, Rechtsanwalt im Netzwerk von Gutachter Koop,</> : 'dem von mir gewählten Rechtsanwalt aus dem Netzwerk von Gutachter Koop'} hiermit Vollmacht zur Vertretung in allen rechtlichen Angelegenheiten bezüglich meines Unfallschadens. Dies umfasst insbesondere die Korrespondenz mit der gegnerischen Haftpflichtversicherung.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Kostenfreiheit</p>
        <p>Die Anwaltskosten werden vollständig von der gegnerischen Haftpflichtversicherung getragen. Für mich entstehen keine Kosten, sofern eine klare Haftungslage vorliegt.</p>
      </div>
      <div>
        <p className="font-semibold mb-1">Umfang</p>
        <p>Der Anwalt ist berechtigt, in meinem Namen Erklärungen abzugeben, Fristen zu setzen und erforderliche rechtliche Schritte einzuleiten. Er wird mich vorab über wichtige Entscheidungen informieren.</p>
      </div>
      <p className="text-[11px] text-gray-500 italic">Mit meiner Unterschrift erteile ich die oben beschriebene Vollmacht.</p>
    </div>
  );
}

// ── Konstanten ────────────────────────────────────────────────────────────────

const BRAND_BLUE = '#1E5FB7';
const BRAND_GREEN = '#3DB54A';

// ── Hauptkomponente ───────────────────────────────────────────────────────────

export function UnterschriftenStep({
  sessionId,
  kundenName,
  kennzeichen,
  demontageNoetig,
  ersatzfahrzeugGewaehlt,
  anwaltName,
  onComplete,
  onBack,
}: Props) {
  const [phase, setPhase] = useState<Phase>('liste');
  const [aktiverIndex, setAktiverIndex] = useState(0);
  const [unterschriften, setUnterschriften] = useState<Record<string, string>>({});
  const [hatGescrollt, setHatGescrollt] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Aktive Dokumente berechnen
  const aktiveDokumente: DokumentDef[] = [
    {
      id: 'gutachten',
      titel: 'Gutachten-Auftrag & Honorarvereinbarung',
      untertitel: 'Auftrag an SV-Büro Koop, BVSK-Honorartabelle',
      inhalt: <InhaltGutachten kundenName={kundenName} kennzeichen={kennzeichen} />,
    },
    ...(demontageNoetig
      ? [{
          id: 'demontage' as DokumentId,
          titel: 'Demontage- und Vermessungsauftrag',
          untertitel: 'LCS Werkstatt — für genaue Begutachtung',
          inhalt: <InhaltDemontage kundenName={kundenName} kennzeichen={kennzeichen} />,
        }]
      : []),
    ...(ersatzfahrzeugGewaehlt
      ? [{
          id: 'mietwagen' as DokumentId,
          titel: 'Mietwagenvertrag MietGo + Abtretung',
          untertitel: 'Ersatzfahrzeug während der Reparatur',
          inhalt: <InhaltMietwagen kundenName={kundenName} kennzeichen={kennzeichen} />,
        }]
      : []),
    {
      id: 'anwalt',
      titel: 'Anwaltsvollmacht',
      untertitel: 'Vollmacht für den Korrespondenzanwalt',
      inhalt: <InhaltAnwalt kundenName={kundenName} anwaltName={anwaltName} />,
    },
  ];

  const anzahl = aktiveDokumente.length;
  const anzahlSigned = Object.keys(unterschriften).length;
  const aktivesDoc = aktiveDokumente[aktiverIndex];

  // Signature Pad initialisieren
  useEffect(() => {
    if (phase !== 'unterschreiben' || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
      minWidth: 0.5,
      maxWidth: 2.5,
    });
    pad.addEventListener('endStroke', () => setHasSignature(!pad.isEmpty()));
    signaturePadRef.current = pad;
    return () => { pad.off(); signaturePadRef.current = null; };
  }, [phase, aktiverIndex]);

  // Scroll-Gate: Prüfen ob Nutzer bis zum Ende gescrollt hat
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
    if (atBottom) setHatGescrollt(true);
  }, []);

  // Zum Lesen-Modus wechseln
  const startLesen = (index: number) => {
    setAktiverIndex(index);
    setHatGescrollt(false);
    setPhase('lesen');
  };

  // Zum Unterschreiben-Modus wechseln
  const startUnterschreiben = () => {
    setHasSignature(false);
    setPhase('unterschreiben');
  };

  // Unterschrift löschen
  const clearSignature = () => {
    signaturePadRef.current?.clear();
    setHasSignature(false);
  };

  // Unterschrift speichern
  const handleSave = async () => {
    if (!hasSignature || !signaturePadRef.current) return;
    const pngDataUrl = signaturePadRef.current.toDataURL('image/png');
    setIsSaving(true);
    try {
      const neueUnterschriften = { ...unterschriften, [aktivesDoc.id]: pngDataUrl };

      if (sessionId) {
        const { error } = await supabase
          .from('kiosk_sessions')
          .update({ unterschriften: neueUnterschriften })
          .eq('id', sessionId);
        if (error) throw error;
      }

      setUnterschriften(neueUnterschriften);

      const alleSigniert = aktiveDokumente.every(d => neueUnterschriften[d.id]);
      if (alleSigniert) {
        toast.success('Alle Dokumente unterzeichnet!');
        onComplete(neueUnterschriften);
        return;
      }

      // Weiter zum nächsten noch nicht signierten Dokument
      const nextIndex = aktiveDokumente.findIndex(d => !neueUnterschriften[d.id]);
      if (nextIndex !== -1) {
        setAktiverIndex(nextIndex);
      }
      setPhase('liste');
    } catch (err) {
      console.error('Unterschrift speichern fehlgeschlagen:', err);
      toast.error('Fehler beim Speichern der Unterschrift');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render: Liste ──────────────────────────────────────────────────────────

  if (phase === 'liste') {
    return (
      <div className="space-y-6" data-testid="unterschriften-step">
        {/* Header */}
        <div className="text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg"
            style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)` }}
          >
            <FileText size={28} className="text-white" />
          </div>
          <h2 className="text-[22px] font-bold text-gray-900">Letzter Schritt — Unterschriften</h2>
          <p className="text-[14px] text-gray-500 mt-1 max-w-sm mx-auto">
            Bitte lesen und unterzeichnen Sie {anzahl === 1 ? 'das folgende Dokument' : `die folgenden ${anzahl} Dokumente`}.
          </p>
        </div>

        {/* Progress-Bar */}
        <div
          className="flex items-center gap-3 bg-white rounded-2xl border-2 border-gray-200 px-5 py-4 shadow-sm"
          data-testid="unterschriften-progress"
        >
          <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${(anzahlSigned / anzahl) * 100}%`,
                background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})`,
              }}
            />
          </div>
          <span className="text-[14px] font-bold text-gray-700 tabular-nums">
            {anzahlSigned} / {anzahl}
          </span>
        </div>

        {/* Dokument-Liste */}
        <div className="space-y-3" data-testid="dokument-liste">
          {aktiveDokumente.map((dok, i) => {
            const signed = !!unterschriften[dok.id];
            return (
              <button
                key={dok.id}
                data-testid={`dokument-item-${dok.id}`}
                onClick={() => !signed && startLesen(i)}
                className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all"
                style={{
                  minHeight: 72,
                  borderColor: signed ? BRAND_GREEN : '#E5E7EB',
                  background: signed ? '#F0FDF4' : '#FFFFFF',
                  cursor: signed ? 'default' : 'pointer',
                }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: signed ? BRAND_GREEN : '#F3F4F6',
                  }}
                >
                  {signed
                    ? <CheckCircle2 size={20} className="text-white" />
                    : <FileText size={20} className="text-gray-400" />
                  }
                </div>
                <div className="flex-1">
                  <p className="text-[14px] font-semibold" style={{ color: signed ? '#15803D' : '#111827' }}>
                    {dok.titel}
                  </p>
                  <p className="text-[12px] text-gray-500 mt-0.5">{dok.untertitel}</p>
                </div>
                {!signed && (
                  <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
                )}
                {signed && (
                  <span className="text-[11px] font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full flex-shrink-0">
                    ✓ Unterzeichnet
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Zurück-Button (nur wenn noch nicht angefangen) */}
        {anzahlSigned === 0 && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
            style={{ minHeight: 52 }}
          >
            <ArrowLeft size={16} /> Zurück
          </button>
        )}
      </div>
    );
  }

  // ── Render: Lesen ──────────────────────────────────────────────────────────

  if (phase === 'lesen') {
    return (
      <div className="space-y-4" data-testid="unterschriften-step">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPhase('liste')}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-all flex-shrink-0"
          >
            <ArrowLeft size={18} className="text-gray-500" />
          </button>
          <div>
            <h2 className="text-[17px] font-bold text-gray-900 leading-tight">{aktivesDoc.titel}</h2>
            <p className="text-[12px] text-gray-500">{aktivesDoc.untertitel}</p>
          </div>
        </div>

        {/* Dokument-Inhalt (scrollbar mit Scroll-Gate) */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          data-testid="dokument-lesen"
          className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-y-auto"
          style={{ maxHeight: '52vh', WebkitOverflowScrolling: 'touch' }}
        >
          <div className="p-5">
            {aktivesDoc.inhalt}
          </div>
          {/* Scroll-Hinweis am Ende */}
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 text-center">
            <p className="text-[11px] text-gray-400">
              {hatGescrollt
                ? '✓ Dokument vollständig gelesen'
                : '↓ Bitte bis zum Ende scrollen, um fortzufahren'}
            </p>
          </div>
        </div>

        {/* Weiter zur Unterschrift */}
        <button
          data-testid="weiter-zur-unterschrift-btn"
          onClick={startUnterschreiben}
          disabled={!hatGescrollt}
          className="w-full py-4 rounded-2xl text-[15px] font-bold text-white shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: hatGescrollt
              ? `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`
              : '#9CA3AF',
            minHeight: 56,
          }}
        >
          <div className="flex items-center justify-center gap-2">
            <Pen size={18} />
            Hier unterschreiben
          </div>
        </button>
      </div>
    );
  }

  // ── Render: Unterschreiben ─────────────────────────────────────────────────

  return (
    <div className="space-y-4" data-testid="unterschriften-step">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => { setPhase('lesen'); setHatGescrollt(true); }}
          className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-all flex-shrink-0"
        >
          <ArrowLeft size={18} className="text-gray-500" />
        </button>
        <div>
          <h2 className="text-[17px] font-bold text-gray-900 leading-tight">Unterschrift</h2>
          <p className="text-[12px] text-gray-500">{aktivesDoc.titel}</p>
        </div>
      </div>

      {/* Progress */}
      <div
        className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 px-4 py-2.5"
        data-testid="unterschriften-progress"
      >
        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${(anzahlSigned / anzahl) * 100}%`,
              background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})`,
            }}
          />
        </div>
        <span className="text-[13px] font-bold text-gray-600 tabular-nums">
          {anzahlSigned} / {anzahl}
        </span>
      </div>

      {/* Signatur-Canvas */}
      <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Pen size={14} className="text-gray-500" />
            <span className="text-[12px] font-bold text-gray-700">Unterschrift ({kundenName || 'Auftraggeber'})</span>
          </div>
          {hasSignature && (
            <button
              onClick={clearSignature}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-[10px] font-semibold transition-all"
            >
              <Trash2 size={12} /> Löschen
            </button>
          )}
        </div>
        <div className="relative">
          <canvas
            ref={canvasRef}
            data-testid="signatur-canvas"
            className="w-full touch-none"
            style={{ height: '160px', cursor: 'crosshair', display: 'block' }}
          />
          {!hasSignature && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-[14px] text-gray-300 font-medium">Hier unterschreiben</p>
            </div>
          )}
        </div>
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <p className="text-[10px] text-gray-400">
            {new Date().toLocaleDateString('de-DE')} · Touch oder Stylus
          </p>
        </div>
      </div>

      {/* Speichern-Button */}
      <button
        data-testid="unterschrift-speichern-btn"
        onClick={handleSave}
        disabled={!hasSignature || isSaving}
        className="w-full py-4 rounded-2xl text-[15px] font-bold text-white shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          background: `linear-gradient(135deg, ${BRAND_GREEN}, #2D8F3C)`,
          minHeight: 56,
        }}
      >
        {isSaving
          ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Wird gespeichert…
            </div>
          )
          : (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 size={18} />
              {anzahlSigned + 1 === anzahl ? 'Abschließen' : 'Weiter zum nächsten Dokument'}
            </div>
          )
        }
      </button>
    </div>
  );
}
